import type React from "react";
import { ExpandableText } from "@/components/common/ExpandableText";
import { PostCardEditForm } from "@/components/common/PostCardEditForm";
import type { EditableFile } from "@/components/common/ExistingFileAttachment";
import { EventLinkPreviewProvider } from "@/contexts/EventLinkPreviewContext";
import type { EventApiResponse } from "@/lib/humhub-api/types";

interface PostCardBodyProps {
  isCompact: boolean;
  firstLine: string;
  isEditMode: boolean;
  editedMessage: string;
  content: string;
  isSubmittingEdit: boolean;
  onEditedMessageChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onCancelEdit: () => void;
  onSubmitEdit: (content?: string) => Promise<void>;
  onEventClick?: (eventData: EventApiResponse) => void;
  studyGroupId?: number;
  showOriginalContent?: boolean;
  existingFiles?: EditableFile[];
  filesToRemove?: Set<number>;
  filesToReplace?: Map<number, File>;
  filesToAdd?: File[];
  addFilesInputRef?: React.RefObject<HTMLInputElement | null>;
  onRemoveExistingFile?: (fileId: number) => void;
  onReplaceExistingFile?: (fileId: number, newFile: File) => void;
  onRemoveNewFile?: (index: number) => void;
  onAddFilesInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const PostCardBody: React.FC<PostCardBodyProps> = ({
  isCompact,
  firstLine,
  isEditMode,
  editedMessage,
  content,
  isSubmittingEdit,
  onEditedMessageChange,
  onKeyDown,
  onCancelEdit,
  onSubmitEdit,
  onEventClick,
  studyGroupId,
  showOriginalContent = false,
  existingFiles,
  filesToRemove,
  filesToReplace,
  filesToAdd,
  addFilesInputRef,
  onRemoveExistingFile,
  onReplaceExistingFile,
  onRemoveNewFile,
  onAddFilesInputChange,
}) => {
  if (isCompact) {
    return (
      <p className="line-clamp-1 text-sm text-variable-collection-black-gray">
        {firstLine}
      </p>
    );
  }

  if (isEditMode) {
    return (
      <PostCardEditForm
        value={editedMessage}
        originalContent={content}
        isSubmitting={isSubmittingEdit}
        onChange={onEditedMessageChange}
        onKeyDown={onKeyDown}
        onCancel={onCancelEdit}
        onSubmit={onSubmitEdit}
        existingFiles={existingFiles}
        filesToRemove={filesToRemove}
        filesToReplace={filesToReplace}
        filesToAdd={filesToAdd}
        addFilesInputRef={addFilesInputRef}
        onRemoveExistingFile={onRemoveExistingFile}
        onReplaceExistingFile={onReplaceExistingFile}
        onRemoveNewFile={onRemoveNewFile}
        onAddFilesInputChange={onAddFilesInputChange}
      />
    );
  }

  if (onEventClick || studyGroupId) {
    return (
      <EventLinkPreviewProvider
        onEventClick={onEventClick}
        currentStudyGroupId={studyGroupId ? String(studyGroupId) : undefined}
        showOriginalContent={showOriginalContent}
      >
        <ExpandableText content={content} />
      </EventLinkPreviewProvider>
    );
  }

  return <ExpandableText content={content} />;
};
